import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
  Inbox, ClipboardList, FolderOpen, MessageSquare, Calendar, ArrowRight, Sparkles,
  CircleDot, AlertTriangle, CheckCircle2, Send, Pencil, Clock4, FileText, Image as ImageIcon, FileBox,
  GanttChart, Boxes,
} from "lucide-react";
// SEED_LEADS — legacy hardcode replaced by getStudioLeads() (derived live).
import {
  getActiveTasks, getActiveFiles, getActiveComms, getActiveProjectMeta,
  type DerivedTask, type DerivedAsset, type DerivedComm, type TaskStatus,
} from "../lib/studioFromGraph";
import { LeadsKanban } from "../components/admin/LeadsKanban";
import { TrackerAgenticStrip } from "../components/tracker/TrackerAgenticStrip";
import { MindmapView } from "../components/tracker/MindmapView";
import { GanttView } from "../components/tracker/GanttView";
import { BOMView } from "../components/tracker/BOMView";
import { ApprovalsView } from "../components/tracker/ApprovalsView";
import { ContextualChat } from "../components/tracker/ContextualChat";
import { getStudioLeads } from "../lib/studioFromGraph";
import { useActiveProjectGraph } from "../lib/useActiveProjectGraph";
import { BrandIcon, type BrandSlug } from "../components/BrandIcon";
import { cls } from "../lib/cls";

/* Re-run the live derivers whenever the active project changes — the
   ProjectTracker in the global header dispatches `foyrmind:projectChanged`
   on every pick. */
function useActiveProjectSignal(): number {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const on = (): void => setTick((t) => t + 1);
    window.addEventListener("foyrmind:projectChanged", on);
    window.addEventListener("storage", on);
    return () => {
      window.removeEventListener("foyrmind:projectChanged", on);
      window.removeEventListener("storage", on);
    };
  }, []);
  return tick;
}

/* ──────────────────────────────────────────────────────────────────
   Foyr-Manage parity surface — every feature on their /manage page
   that we didn't already cover lives here.
     • Leads + pipeline       ← matches their Lead Capture + Pipeline
     • Tasks (with stuck reasons + owners) ← Task Management
     • Files                  ← File Management
     • Drafted comms          ← Email AI Agent + Follow-ups
   Where they ship templates, we ship grounded drafts: each comm draft
   carries the memory / rule it cited so it's accept-or-edit, not
   trust-me.
   ────────────────────────────────────────────────────────────────── */

type StudioTab = "leads" | "mindmap" | "gantt" | "bom" | "approvals" | "tasks" | "files" | "comms";

/** Tab → specialist agent. Chat is intrinsic to every tab (rendered
 *  once at layout level) — this map picks the persona biased to the
 *  current surface. Single source of truth, no per-view literals. */
const TAB_TO_AGENT: Readonly<Record<StudioTab, string>> = {
  leads:     "schedule-synth",
  mindmap:   "style-cohesion",
  gantt:     "schedule-synth",
  bom:       "budget-pull",
  approvals: "approval-age",
  tasks:     "schedule-synth",
  files:     "spatial-fit-check",
  comms:     "approval-age",
};

export default function AdminStudio(): JSX.Element {
  const [tab, setTab] = useState<StudioTab>("leads");
  const signal = useActiveProjectSignal();
  const meta = useMemo(() => getActiveProjectMeta(), [signal]);
  const tasks = useMemo(() => getActiveTasks(), [signal]);
  const files = useMemo(() => getActiveFiles(), [signal]);
  const comms = useMemo(() => getActiveComms(), [signal]);
  const leads = useMemo(() => getStudioLeads(), [signal]);
  // Milestone count is project-scoped — read live from the active graph.
  const { graph: activeGraph } = useActiveProjectGraph();
  const milestoneCount = useMemo(
    () => activeGraph.nodes.filter((n) => n.kind === "Milestone").length,
    [activeGraph.nodes],
  );
  const skuCount = useMemo(
    () => activeGraph.nodes.filter((n) => n.kind === "SKU").length,
    [activeGraph.nodes],
  );
  const approvalCount = useMemo(
    () => activeGraph.nodes.filter((n) => n.kind === "Approval").length,
    [activeGraph.nodes],
  );
  const subTabs: ReadonlyArray<{ id: StudioTab; label: string; icon: JSX.Element; count: number }> = [
    { id: "leads",   label: "Leads",   icon: <Inbox          className="w-3.5 h-3.5" />, count: leads.length },
    { id: "mindmap", label: "Mindmap", icon: <Sparkles       className="w-3.5 h-3.5" />, count: leads.length },
    { id: "gantt",   label: "Gantt",   icon: <GanttChart     className="w-3.5 h-3.5" />, count: milestoneCount },
    { id: "bom",     label: "BOM",     icon: <Boxes          className="w-3.5 h-3.5" />, count: skuCount },
    { id: "approvals", label: "Approvals", icon: <CheckCircle2 className="w-3.5 h-3.5" />, count: approvalCount },
    { id: "tasks",   label: "Tasks",   icon: <ClipboardList  className="w-3.5 h-3.5" />, count: tasks.length },
    { id: "files",   label: "Files",   icon: <FolderOpen     className="w-3.5 h-3.5" />, count: files.length },
    { id: "comms",   label: "Comms",   icon: <MessageSquare  className="w-3.5 h-3.5" />, count: comms.length },
  ];
  return (
    <div>
      {meta ? (
        <div className="mb-3">
          {/* Editorial header — Apple New York serif via ui-serif. */}
          <h2 className="font-newyork text-ink-950 text-[26px] leading-[1.1] tracking-tight">
            {meta.name}
          </h2>
          <div className="font-newyork text-ink-500 text-[14px] italic leading-tight mt-1">
            {meta.city} · {meta.client}
          </div>
        </div>
      ) : null}
      <div className="flex items-center gap-1.5 mb-3 flex-wrap">
        {subTabs.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={cls(
              "pill text-[11.5px] border inline-flex items-center gap-1.5",
              tab === t.id
                ? "bg-ink-950 text-cream border-ink-950"
                : "bg-cream-dark border-border text-ink-700 hover:border-foyr/40 hover:text-ink-950",
            )}
          >
            {t.icon} {t.label}
            <span className={cls(
              "pill !py-0 !px-1.5 text-[10px] tabular",
              tab === t.id ? "bg-cream/15 text-cream" : "bg-cream border-border text-ink-700",
            )}>{t.count}</span>
          </button>
        ))}
      </div>

      {tab === "leads" ? (
        <>
          <TrackerAgenticStrip />
          <LeadsKanban />
        </>
      ) : null}
      {/* ContextualChat is INTRINSIC — present on every tab, biased to
          the active surface via TAB_TO_AGENT. Default-collapsed, the
          chip persona name updates as the tab changes. */}
      <ContextualChat surfaceId={`tracker-${tab}`} defaultAgentId={TAB_TO_AGENT[tab]} />

      {tab === "mindmap" ? <MindmapView /> : null}
      {tab === "gantt"   ? <GanttView /> : null}
      {tab === "bom"     ? <BOMView /> : null}
      {tab === "approvals" ? <ApprovalsView /> : null}
      {tab === "tasks"   ? <TasksBoard tasks={tasks} /> : null}
      {tab === "files" ? <FilesBoard files={files} /> : null}
      {tab === "comms" ? <CommsBoard comms={comms} /> : null}
    </div>
  );
}

/* ── leads ──────────────────────────────────────────────────────── */

/* ── tasks ──────────────────────────────────────────────────────── */
function TasksBoard({ tasks }: { tasks: readonly DerivedTask[] }): JSX.Element {
  const cols: { id: TaskStatus; label: string; tone: string; icon: JSX.Element }[] = [
    { id: "todo",    label: "To do",   tone: "border-border",     icon: <CircleDot className="w-3 h-3 text-ink-500" /> },
    { id: "doing",   label: "Doing",   tone: "border-sea/40",     icon: <Clock4 className="w-3 h-3 text-sea" /> },
    { id: "blocked", label: "Blocked", tone: "border-foyr/40",    icon: <AlertTriangle className="w-3 h-3 text-foyr" /> },
    { id: "done",    label: "Done",    tone: "border-fern/40",    icon: <CheckCircle2 className="w-3 h-3 text-fern" /> },
  ];
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2">
      {cols.map((c) => {
        const colTasks = tasks.filter((t) => t.status === c.id);
        return (
          <div key={c.id} className={cls("panel p-2 border", c.tone)}>
            <div className="flex items-center justify-between mb-2">
              <div className="inline-flex items-center gap-1 text-[11px] uppercase tracking-wider text-ink-700">{c.icon} {c.label}</div>
              <span className="pill bg-cream-dark border border-border text-ink-700 text-[10px] tabular">{colTasks.length}</span>
            </div>
            <div className="space-y-1.5">
              {colTasks.length === 0 ? (
                <div className="text-[10.5px] text-ink-500 px-1 py-2">No tasks in this column.</div>
              ) : null}
              {colTasks.map((t) => (
                <motion.div key={t.id} initial={{ opacity: 0, y: 2 }} animate={{ opacity: 1, y: 0 }} className="border border-border rounded-lg p-2 bg-cream-dark/40">
                  <div className="text-[12.5px] text-ink-950 leading-snug">{t.title}</div>
                  <div className="text-[10.5px] text-ink-500 tabular mt-1 flex items-center gap-1 flex-wrap">
                    <Calendar className="w-2.5 h-2.5" /> {new Date(t.due).toLocaleDateString()}
                    <span className="text-ink-300">·</span>
                    <span className="pill bg-cream-dark border border-border text-ink-700 text-[10px]">{t.owner}</span>
                    <span className="text-ink-300">·</span>
                    <span className="pill bg-cream border border-border text-ink-500 text-[9.5px] font-mono" title={`MCP tool: ${t.backing_skill}`}>{t.backing_skill}</span>
                  </div>
                  {t.why_stuck ? (
                    <div className="text-[10.5px] text-foyr-deep mt-1 inline-flex items-center gap-1">
                      <AlertTriangle className="w-2.5 h-2.5" /> {t.why_stuck}
                    </div>
                  ) : null}
                  {t.cited_nodes.length > 0 ? (
                    <div className="text-[9.5px] text-ink-400 mt-1 font-mono truncate" title={`Cites: ${t.cited_nodes.join(", ")}`}>
                      cites · {t.cited_nodes.slice(0, 3).join(" · ")}
                    </div>
                  ) : null}
                  <div
                    className="mt-1.5 pt-1.5 border-t border-border/40 text-[10px] text-ink-600 leading-snug"
                    title={`Source: ${t.reason.source}`}
                  >
                    <span className="uppercase tracking-wider text-[9px] text-ink-400 mr-1">why</span>
                    {t.reason.why}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ── files ──────────────────────────────────────────────────────── */
function FilesBoard({ files }: { files: readonly DerivedAsset[] }): JSX.Element {
  const kindIcon: Record<string, JSX.Element> = {
    moodboard: <ImageIcon className="w-3 h-3 text-foyr" />,
    render:    <ImageIcon className="w-3 h-3 text-foyr" />,
    boq:       <FileText className="w-3 h-3 text-foyr" />,
    contract:  <FileText className="w-3 h-3 text-foyr" />,
    drawing:   <FileBox className="w-3 h-3 text-foyr" />,
  };
  return (
    <div className="panel p-0 overflow-hidden">
      <table className="w-full text-[12.5px]">
        <thead className="bg-cream-dark text-ink-500">
          <tr>
            <th className="text-left font-normal py-2 px-3">Asset</th>
            <th className="text-left font-normal py-2 px-3">Kind</th>
            <th className="text-left font-normal py-2 px-3">Source</th>
            <th className="text-left font-normal py-2 px-3">By</th>
            <th className="text-right font-normal py-2 px-3">Size</th>
            <th className="text-right font-normal py-2 px-3">When</th>
          </tr>
        </thead>
        <tbody>
          {files.length === 0 ? (
            <tr><td colSpan={6} className="py-4 px-3 text-center text-ink-500 text-[12px]">No files yet for this project.</td></tr>
          ) : null}
          {files.map((a, i) => (
            <motion.tr key={a.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: i * 0.02 }} className="border-t border-border/60">
              <td className="py-2 px-3">
                <div className="font-mono text-ink-950">{a.name}</div>
                <div className="text-[10px] text-ink-500 mt-0.5 leading-snug" title={`Source: ${a.reason.source}`}>
                  <span className="uppercase tracking-wider text-[9px] text-ink-400 mr-1">why</span>{a.reason.why}
                </div>
              </td>
              <td className="py-2 px-3"><span className="pill bg-cream-dark border border-border text-ink-700 text-[10px] inline-flex items-center gap-1">{kindIcon[a.kind]} {a.kind}</span></td>
              <td className="py-2 px-3">
                <span className="inline-flex items-center gap-1.5 text-ink-700">
                  <BrandIcon slug={a.source_app as BrandSlug} size={14} title={a.source_app} />
                  <span className="text-[11px]">{a.source_app.replace(/-/g, " ")}</span>
                </span>
              </td>
              <td className="py-2 px-3 text-ink-700">{a.uploaded_by}</td>
              <td className="py-2 px-3 text-right tabular text-ink-700">{(a.size_kb / 1024).toFixed(2)} MB</td>
              <td className="py-2 px-3 text-right tabular text-ink-500">{new Date(a.uploaded_at).toLocaleDateString()}</td>
            </motion.tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ── comms ──────────────────────────────────────────────────────── */
function CommsBoard({ comms }: { comms: readonly DerivedComm[] }): JSX.Element {
  if (comms.length === 0) {
    return (
      <div className="panel p-6 text-center text-ink-500 text-[12.5px]">
        No drafts pending for this project — nothing risky enough to nudge.
      </div>
    );
  }
  return (
    <div className="space-y-2">
      {comms.map((c) => (
        <motion.div key={c.id} initial={{ opacity: 0, y: 2 }} animate={{ opacity: 1, y: 0 }} className="panel p-3">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-1">
            <div className="flex items-center gap-1.5">
              <Pencil className="w-3.5 h-3.5 text-foyr" />
              <span className="text-[11px] uppercase tracking-wider text-ink-500">Drafted by agent · awaiting review</span>
              <span className="pill bg-cream-dark border border-border text-ink-500 text-[9.5px] font-mono" title={`MCP tool: ${c.backing_skill}`}>{c.backing_skill}</span>
            </div>
            <span className="pill bg-cream-dark border border-border text-ink-700 text-[10px]">{c.status}</span>
          </div>
          <div className="text-[13px] text-ink-950">
            <strong>To:</strong> {c.to}
          </div>
          <div className="text-[13.5px] font-semibold text-ink-950 mt-0.5">{c.subject}</div>
          <p className="text-[12.5px] text-ink-700 mt-1 leading-relaxed">{c.preview}</p>
          {c.cited_nodes.length > 0 ? (
            <div className="text-[9.5px] text-ink-400 mt-1 font-mono">
              cites · {c.cited_nodes.slice(0, 4).join(" · ")}
            </div>
          ) : null}
          <div className="mt-2 pt-2 border-t border-border/40 text-[10.5px] text-ink-600 leading-snug" title={`Source: ${c.reason.source}`}>
            <span className="uppercase tracking-wider text-[9px] text-ink-400 mr-1">why</span>
            {c.reason.why}
          </div>
          <div className="flex items-center gap-1.5 mt-2">
            <button className="btn btn-foyr !text-[11px] !py-1"><Send className="w-3 h-3" /> Send</button>
            <button className="btn btn-outline !text-[11px] !py-1">Edit</button>
            <button className="btn btn-ghost !text-[11px] !py-1">Discard</button>
            <span className="text-[10.5px] text-ink-500 ml-auto inline-flex items-center gap-1">
              Project <ArrowRight className="w-3 h-3" /> {c.project_id}
            </span>
          </div>
        </motion.div>
      ))}
    </div>
  );
}
